import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  alpha,
  useTheme,
} from "@mui/material/styles";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import AndroidRoundedIcon from "@mui/icons-material/AndroidRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ScrollableTableContainer from "../../../components/common/ScrollableTableContainer";
import {
  useDeleteWhiteLabelCompanyMutation,
  useGetWhiteLabelCompaniesQuery,
} from "../../../redux/admin/whiteLabelApi";
import WhiteLabelCompanyDialog from "./WhiteLabelCompanyDialog";
import WhiteLabelCompanyDetailsDialog from "./WhiteLabelCompanyDetailsDialog";
import { toast } from "react-hot-toast";

const STATUS_COPY = Object.freeze({
  active: { label: "Active", color: "success" },
  inactive: { label: "Inactive", color: "default" },
  live: { label: "Live", color: "success" },
  pending: { label: "Pending", color: "warning" },
  building: { label: "Building", color: "warning" },
  failed: { label: "Failed", color: "error" },
  idle: { label: "Idle", color: "default" },
  offline: { label: "Offline", color: "default" },
});

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const getCompanyInitials = (value) =>
  String(value || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const getDomainCount = (company) => {
  const customDomains = Array.isArray(company?.domains?.customDomains)
    ? company.domains.customDomains.filter(Boolean)
    : [];

  return [
    company?.domains?.primary,
    company?.domains?.admin,
    company?.domains?.api,
    company?.domains?.landing,
    company?.domains?.tracking,
    ...customDomains,
  ].filter(Boolean).length;
};

const getWebsiteStatus = (company) =>
  company?.website?.websiteUrl ? "live" : "offline";

const getAppStatus = (company) => {
  const status = String(company?.buildCenter?.androidStatus || "").trim().toLowerCase();
  return status || "idle";
};

function StatusChip({ value }) {
  const meta = STATUS_COPY[value] || {
    label: String(value || "Unknown"),
    color: "default",
  };

  return (
    <Chip
      size="small"
      label={meta.label}
      color={meta.color}
      variant={meta.color === "default" ? "outlined" : "filled"}
      sx={{ fontWeight: 700 }}
    />
  );
}

function CompanySkeletonCard() {
  return (
    <Paper elevation={0} sx={{ borderRadius: 3, p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Skeleton variant="circular" width={44} height={44} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="55%" height={28} />
            <Skeleton width="35%" height={20} />
          </Box>
        </Stack>
        <Skeleton width="90%" height={22} />
        <Skeleton width="65%" height={22} />
        <Skeleton width="45%" height={22} />
      </Stack>
    </Paper>
  );
}

function CompanyCard({
  company,
  theme,
  onView,
  onEdit,
  onDelete,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        bgcolor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.9)
            : alpha(theme.palette.common.white, 0.92),
        p: 2,
      }}
    >
      <Stack spacing={1.8}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              fontWeight: 900,
            }}
          >
            {getCompanyInitials(company.companyName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {company.companyName}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {company.companyCode}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <StatusChip value={company.status} />
          <StatusChip value={getWebsiteStatus(company)} />
          <StatusChip value={getAppStatus(company)} />
        </Stack>

        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Domain Count
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {getDomainCount(company)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Created Date
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatDate(company.createdAt)}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => onView(company)}
              sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.85)}` }}
            >
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit(company)}
              sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.85)}` }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => onDelete(company)}
              sx={{
                border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                color: "error.main",
              }}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function WhiteLabelCompaniesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [dialogState, setDialogState] = useState({ mode: null, companyId: null });
  const [detailsCompanyId, setDetailsCompanyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const queryArgs = useMemo(
    () => ({
      page,
      limit: rowsPerPage,
      search: String(deferredSearchQuery || "").trim() || undefined,
      status: statusFilter === "all" ? undefined : statusFilter,
      sortBy: "createdAt",
      sortOrder: "desc",
    }),
    [page, rowsPerPage, deferredSearchQuery, statusFilter]
  );

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetWhiteLabelCompaniesQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const [deleteCompany, { isLoading: isDeleting }] =
    useDeleteWhiteLabelCompanyMutation();

  const companies = useMemo(() => data?.items || [], [data?.items]);
  const pagination = useMemo(
    () =>
      data?.pagination || {
        currentPage: 1,
        limit: rowsPerPage,
        totalCount: 0,
        totalPages: 1,
        hasMore: false,
      },
    [data?.pagination, rowsPerPage]
  );

  useEffect(() => {
    const totalPages = Math.max(1, Number(pagination.totalPages || 1));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, pagination.totalPages]);

  useEffect(() => {
    if (!error) return;
    toast.error(error?.data?.msg || "Failed to load companies.");
  }, [error]);

  useEffect(() => {
    if (!deleteError) return;
    toast.error(deleteError);
  }, [deleteError]);

  const summaryCards = useMemo(
    () => [
      {
        key: "total",
        label: "Total Companies",
        value: pagination.totalCount || 0,
        icon: <ApartmentRoundedIcon fontSize="small" />,
      },
      {
        key: "domains",
        label: "Connected Domains",
        value: companies.reduce((count, item) => count + getDomainCount(item), 0),
        icon: <LanguageRoundedIcon fontSize="small" />,
      },
      {
        key: "websites",
        label: "Websites Live",
        value: companies.filter((item) => getWebsiteStatus(item) === "live").length,
        icon: <PublicRoundedIcon fontSize="small" />,
      },
      {
        key: "apps",
        label: "Apps Active",
        value: companies.filter((item) =>
          ["live", "pending", "building"].includes(getAppStatus(item))
        ).length,
        icon: <AndroidRoundedIcon fontSize="small" />,
      },
    ],
    [companies, pagination.totalCount]
  );

  const handleCreate = () => {
    setDialogState({ mode: "create", companyId: null });
  };

  const handleEdit = (company) => {
    setDialogState({ mode: "edit", companyId: company?._id });
  };

  const handleView = (company) => {
    setDetailsCompanyId(company?._id || null);
  };

  const handleDelete = (company) => {
    setDeleteTarget(company);
    setDeleteError("");
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?._id) return;

    try {
      await deleteCompany(deleteTarget._id).unwrap();
      toast.success("Company deleted successfully.");
      setDeleteTarget(null);
      setDeleteError("");
    } catch (deleteRequestError) {
      setDeleteError(
        deleteRequestError?.data?.msg ||
          deleteRequestError?.data?.message ||
          "Failed to delete company."
      );
    }
  };

  return (
    <>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", xl: "row" }}
          spacing={2}
          justifyContent="space-between"
        >
          <Box sx={{ maxWidth: 760 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.75 }}>
              Company Management
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Manage white-label companies using the existing admin shell, shared
              table layout, and connected backend APIs.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {summaryCards.map((card) => (
              <Paper
                key={card.key}
                elevation={0}
                sx={{
                  minWidth: 152,
                  borderRadius: 3,
                  px: 1.75,
                  py: 1.4,
                  border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.common.white, 0.03)
                      : alpha(theme.palette.common.white, 0.88),
                }}
              >
                <Stack direction="row" spacing={1.15} alignItems="center">
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.2 }}>
                      {card.label}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      {card.value}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.92)
                : alpha(theme.palette.common.white, 0.96),
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 18px 40px ${alpha("#020617", 0.28)}`
                : `0 18px 40px ${alpha("#0f172a", 0.06)}`,
          }}
        >
          <Box
            sx={{
              p: { xs: 2, md: 2.25 },
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
            }}
          >
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: "column", xl: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", xl: "center" }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
                  Company Directory ({pagination.totalCount || 0})
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 122 }}>
                    <Select
                      value={rowsPerPage}
                      onChange={(event) => {
                        setRowsPerPage(Number(event.target.value));
                        setPage(1);
                      }}
                    >
                      {[5, 10, 25].map((size) => (
                        <MenuItem key={size} value={size}>
                          {size} per page
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Tooltip title="Refresh">
                    <span>
                      <IconButton
                        onClick={() => refetch()}
                        disabled={isFetching}
                        sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.85)}` }}
                      >
                        <RefreshRoundedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={handleCreate}
                  >
                    Create Company
                  </Button>
                </Stack>
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search companies by name, code, domain, or support email..."
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setPage(1);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchRoundedIcon sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2.5,
                    },
                  }}
                />

                <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 180 } }}>
                  <Select
                    value={statusFilter}
                    onChange={(event) => {
                      setStatusFilter(event.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Box>

          {error ? (
            <Box sx={{ p: 2.5 }}>
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
              >
                {error?.data?.msg || "Failed to load companies."}
              </Alert>
            </Box>
          ) : isMobile ? (
            <Stack spacing={1.5} sx={{ p: 2 }}>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <CompanySkeletonCard key={`mobile-skeleton-${index}`} />
                ))
              ) : companies.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    p: 3,
                    textAlign: "center",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.common.white, 0.03)
                        : alpha(theme.palette.primary.main, 0.03),
                  }}
                >
                  <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                    No companies found
                  </Typography>
                  <Typography color="text.secondary">
                    Adjust your search or create a new company to get started.
                  </Typography>
                </Paper>
              ) : (
                companies.map((company) => (
                  <CompanyCard
                    key={company._id}
                    company={company}
                    theme={theme}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </Stack>
          ) : (
            <ScrollableTableContainer sx={{ maxHeight: 580 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      "Company Name",
                      "Company Code",
                      "Status",
                      "Domain Count",
                      "Website Status",
                      "App Status",
                      "Created Date",
                      "Actions",
                    ].map((label) => (
                      <TableCell
                        key={label}
                        sx={{
                          fontWeight: 900,
                          whiteSpace: "nowrap",
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? alpha(theme.palette.background.default, 0.92)
                              : "#f8fafc",
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: rowsPerPage }).map((_, index) => (
                      <TableRow key={`skeleton-row-${index}`}>
                        {Array.from({ length: 8 }).map((__, cellIndex) => (
                          <TableCell key={`skeleton-cell-${index}-${cellIndex}`}>
                            <Skeleton height={26} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : companies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                          No companies found
                        </Typography>
                        <Typography color="text.secondary">
                          Try another filter or create a company.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    companies.map((company) => (
                      <TableRow key={company._id} hover>
                        <TableCell sx={{ minWidth: 240 }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              sx={{
                                width: 38,
                                height: 38,
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: theme.palette.primary.main,
                                fontWeight: 900,
                              }}
                            >
                              {getCompanyInitials(company.companyName)}
                            </Avatar>
                            <Typography sx={{ fontWeight: 700 }}>
                              {company.companyName}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>
                          {company.companyCode}
                        </TableCell>
                        <TableCell>
                          <StatusChip value={company.status} />
                        </TableCell>
                        <TableCell>{getDomainCount(company)}</TableCell>
                        <TableCell>
                          <StatusChip value={getWebsiteStatus(company)} />
                        </TableCell>
                        <TableCell>
                          <StatusChip value={getAppStatus(company)} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatDate(company.createdAt)}
                        </TableCell>
                        <TableCell sx={{ minWidth: 162 }}>
                          <Stack direction="row" spacing={0.75}>
                            <Tooltip title="View">
                              <IconButton size="small" onClick={() => handleView(company)}>
                                <VisibilityRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEdit(company)}>
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(company)}
                              >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
          )}

          <Box
            sx={{
              px: { xs: 2, md: 2.5 },
              py: 1.8,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 1.25,
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
            }}
          >
            <Typography sx={{ color: "text.secondary" }}>
              Showing {companies.length ? (pagination.currentPage - 1) * pagination.limit + 1 : 0} to{" "}
              {Math.min(
                pagination.currentPage * pagination.limit,
                pagination.totalCount || 0
              )} of {pagination.totalCount || 0} companies
            </Typography>
            <Pagination
              page={pagination.currentPage || 1}
              count={Math.max(1, pagination.totalPages || 1)}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              disabled={isLoading}
            />
          </Box>
        </Paper>
      </Stack>

      <WhiteLabelCompanyDialog
        open={Boolean(dialogState.mode)}
        mode={dialogState.mode}
        companyId={dialogState.companyId}
        onClose={() => setDialogState({ mode: null, companyId: null })}
      />

      <WhiteLabelCompanyDetailsDialog
        open={Boolean(detailsCompanyId)}
        companyId={detailsCompanyId}
        onClose={() => setDetailsCompanyId(null)}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (isDeleting) return;
          setDeleteTarget(null);
          setDeleteError("");
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Company</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
            <Typography>
              Are you sure you want to delete{" "}
              <strong>{deleteTarget?.companyName || "this company"}</strong>?
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              This uses the backend soft delete flow and removes the company from the active list.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setDeleteTarget(null);
              setDeleteError("");
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete Company"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
